import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import DynamicHeadTag from "components/__new/common/DynamicHeadTag";
import { ButtonLoader, Input, SelectInput, FileUploadForm } from "components/__new/common/form";
import { ReactComponent as IconAngleLeft } from "assets/images/icons/project-icons/angle-left.svg";
import ComplianceHeader from "components/__new/compliance/ComplianceHeader";
import useBackButton from "hooks/useBackButton";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Maybe from "components/__new/common/Maybe";
import useLoginInfo from "hooks/useLoginInfo";
import validate from "utils/validate";
import PageContent from "components/__new/common/PageContent";
import useUploadDocument from "hooks/compliance/useUploadDocument";
import { addressDocumentLabel } from "data/documentLabel";
import { splitCompanyRegistration } from "utils/splitCompanyRegistration";
import * as company from "store/ui/companies/action";
import * as complianceActions from "store/entities/compliance/action";
import * as accountActions from "store/auth/account/action";


const VerifyBusiness = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const {goBack} = useBackButton();
    const {userInfo} = useLoginInfo();
    const { uploadFile, uploadLoading, addSingleBusinessDocument } = useUploadDocument();
    const { employerInfoLoading, employerInfo, companyInfoUpdateLoading, companyInfoUpdate } = useSelector((s) => s.auth.account);
    const { companiesLoading, companies } = useSelector((s) => s.ui.companies);
    
    
    const registrationTypes = ["Limited Liability", "NGO", "Partnership", "Sole Proprietorship", "Corporation"];
    const [exitAfterSave, setExitAfterSave] = useState(true);
    const [isFormValidated, setIsFormValidated] = useState(false);
    const [isCompanyNameSearched, setIsCompanyNameSearched] = useState(false);
    const [form, setForm] = useState({
        registrationType: "",
        registrationPrefix: "",
        registrationNumber: "",
        fullRegistrationNumber: "",
        businessName: "",
        tradingName: "",
        businessAddress: "",
        cacDocument: "",
        particularsOfDirectorsDocument: "",
        addressVerificationType: "",
        addressVerificationDocument: "",
    })
    
    
    const searchCompanyInfo = (value, type) => {
        let updatedForm = {};
        if (type === "prefix"){
            updatedForm = {
                ...updatedForm,
                registrationPrefix: value,
                fullRegistrationNumber: value + form.registrationNumber,
                businessName: "",
            };
        }
        else if (type === "number"){
            updatedForm = {
                ...updatedForm,
                registrationNumber: value,
                fullRegistrationNumber: form.registrationPrefix + value,
                businessName: "",
            };
        }
        const data = {
            ...form,
            ...updatedForm,
        }
        setForm(data)
        setIsCompanyNameSearched(false);
        checkFormValidation(data);
    }
    
    
    const searchCompany = () => {
        if (companiesLoading === false){
            if (form.registrationPrefix !== "" && form.registrationNumber !== ""){
                const params = {
                    type: form.registrationPrefix.toUpperCase(),
                    searchTerm: form.registrationNumber,
                }
                dispatch(company.searchCompanyInfo(params));
                setIsCompanyNameSearched(true);
            }
            else{
                toast.error("Please select a Prefix and enter the Registration Number");
            }
        }
    }
    
    
    const searchCompanyResponseType = () => {
        if (form.fullRegistrationNumber !== "" && !companiesLoading && companies?.data && Array.isArray(companies?.data) === true && companies?.data?.length > 1){
            return "multiple";
        }
        return "single";
    }
    
    
    const getLoadingStatus = () => {
        if (companyInfoUpdateLoading === true || uploadLoading === true){
            return true;
        }
        return false;
    }
    
    
    const checkFormValidation = (data = form, appendKey, appendValue) => {
        let params = {...data};
        if (appendKey){
            params = {
                ...params,
                [appendKey]: appendValue,
            };
        }
        delete params["fullRegistrationNumber"];
        const errors = validate(params);
        if (errors){
            // console.log("checkFormValidation", errors);
            setIsFormValidated(false);
        }
        else{
            // console.log("all good");
            setIsFormValidated(true);
        }
    }
    
    
    const getEmployerInfo = () => {
        if(!employerInfoLoading && (Object.keys(employerInfo).length === 0 || employerInfo.status !== "success")){
            dispatch(accountActions.getEmployerInfo())
        }
    }
    
    
    const saveDraft = () => {
        submitForm(undefined, false);
    }
    
    
    const submitForm = async (e, exit=true) => {
        e?.preventDefault();
        setExitAfterSave(exit);
        
        let businessDocuments = [
            ...employerInfo?.data?.employer?.company?.businessDocuments ?? [],
        ]
        
        const businessVerification = await doFileUploads("cac", form.cacDocument);
        if (businessVerification !== ""){
            businessDocuments = addSingleBusinessDocument(businessDocuments, "cac", businessVerification);
        }
        
        const directorsVerification = await doFileUploads("particulars-of-directors", form.particularsOfDirectorsDocument);
        if (directorsVerification !== ""){
            businessDocuments = addSingleBusinessDocument(businessDocuments, "particulars-of-directors", directorsVerification);
        }
        
        const addressVerification = await doFileUploads(form.addressVerificationType, form.addressVerificationDocument);
        if (addressVerification !== ""){
            businessDocuments = addSingleBusinessDocument(businessDocuments, form.addressVerificationType, addressVerification);
        }
        
        const paramsCompany = {
            registrationType: form.registrationType,
            registrationNumber: form.fullRegistrationNumber,
            businessName: form.businessName,
            businessAlias: form.tradingName,
            address: form.businessAddress,
            businessDocuments,
        }
        dispatch(accountActions.updateCompanyInfo(paramsCompany));
    }
    
    
    const doFileUploads = async (name, file) => {
        if (file !== "" && typeof file !== "string" && file?.lastModified !== undefined){
            const response = await uploadFile(file, name, userInfo?.company?.companyName);
            if (response?.status === "success"){
                return {name, fileUrl: response?.url ?? ""}
            }
            else{
                toast.error(file?.name + " could not be uploaded");
            }
        }
        return "";
    }
    
    
    const getUploadedDocuments = (businessDocuments) => {
        let cac = "";
        let particularsOfDirectors = "";
        let addressVerificationType = "";
        let addressVerificationDocument = "";
        if (businessDocuments !== ""){
            cac = businessDocuments?.filter(doc => doc?.name === "cac")[0]?.fileUrl ?? "";
            particularsOfDirectors = businessDocuments?.filter(doc => doc?.name === "particulars-of-directors")[0]?.fileUrl ?? "";
            for (const name in addressDocumentLabel){
                const data = businessDocuments?.filter(doc => doc?.name === name);
                if (data.length > 0){
                    addressVerificationType = name;
                    addressVerificationDocument = data[0].fileUrl;
                    break;
                }
            }
        }
        return {cac, particularsOfDirectors, addressVerificationType, addressVerificationDocument};
    }
    
    
    useEffect(() => {
        const {isCompliant, status} = userInfo.employer;
        
        if (isCompliant === true){
            history.push("/compliance/approval/success");
        }
        else if (isCompliant === false && status === "review"){
            history.push("/compliance/registered/approval");
        }
        else{
            dispatch(complianceActions.updateComplianceProgress(25));
            getEmployerInfo();
            checkFormValidation();
        }
        // eslint-disable-next-line
    }, [])
    
    
    useEffect(() => {
        if (isCompanyNameSearched === true && companiesLoading === false){
            if (companies?.status === "success" && companies?.data !== "" && typeof companies?.data === "string"){
                const data = {
                    ...form,
                    businessName: companies.data,
                }
                setForm(data);
                checkFormValidation(data);
            }
        }
        // eslint-disable-next-line
    }, [companiesLoading])
    
    
    useEffect(() => {
        if (employerInfoLoading === false && employerInfo.status === "success"){
            const company = employerInfo?.data?.employer?.company ?? "";
            if (company !== ""){
                const {prefix, number} = splitCompanyRegistration(company?.registrationNumber ?? "");
                const {cac, particularsOfDirectors, addressVerificationType, addressVerificationDocument} = getUploadedDocuments(company?.businessDocuments ?? "");
            
                const data = {
                    ...form,
                    registrationType: company?.registrationType ?? "",
                    registrationPrefix: prefix,
                    registrationNumber: number,
                    fullRegistrationNumber: company?.registrationNumber ?? "",
                    businessName: company?.businessName ?? "",
                    tradingName: company?.businessAlias ?? "",
                    businessAddress: company?.address ?? "",
                    cacDocument: cac,
                    particularsOfDirectorsDocument: particularsOfDirectors,
                    addressVerificationType: addressVerificationType,
                    addressVerificationDocument: addressVerificationDocument,
                }
                setForm(data);
                checkFormValidation(data);
            }
        }
        // eslint-disable-next-line
    }, [employerInfoLoading, employerInfo])
    
    
    useEffect(() => {
        if (companyInfoUpdate.status === "success"){
            dispatch(accountActions.resetUpdateCompanyInfo());
            dispatch(company.resetSearchCompanyInfo());
            dispatch(accountActions.updateEmployerInfoFE(companyInfoUpdate?.data));
            if (exitAfterSave === true){
                history.push("/compliance/registered/verify-director");
            }
            else{
                toast.success("Your data has been saved");
            }
        }
        // eslint-disable-next-line
    }, [companyInfoUpdate])
    
    
    return (
        <div>
            
            <DynamicHeadTag
                headerText="Earnipay Compliance"
                headerDescription="Complete each step to access all Earnipay features"
            />
            
            <ComplianceHeader />
            
            <div className="pt-6 px-6 pb-24 sm:pt-10 sm:px-10">
                <div className="max-w-2xl mx-auto box">
                    
                    <div className="flex justify-start">
                        <div onClick={goBack} className="w-max h-8 pr-2 mr-1 flex items-center cursor-pointer">
                            <IconAngleLeft style={{ height: "16px" }} className="w-max pr-3 m-auto" />
                            Back
                        </div>
                    </div>
                    
                    <div className="pb-2 mt-8 border-b">
                        <div className="font-bold text-xl">
                            Verify Business
                        </div>
                        <div className="text-fade text-sm">
                            Please fill in the details to verify your business
                        </div>
                    </div>
                        
                    <PageContent
                        loading={employerInfoLoading}
                        data={employerInfo}
                        loadingText="Fetching business information..."
                        emptyText="Business information not found"
                        errorText="Unable to fetch business information"
                    />

                    <Maybe condition={employerInfoLoading === false && employerInfo?.status === "success"}>
                        <form onSubmit={submitForm} className="mt-8">
                            <SelectInput label="Registration Type" required={true} value={form.registrationType} onChange={(e) => setForm({...form, registrationType: e.target.value})} validationName="registrationType" checkFormValidation={checkFormValidation}>
                                <option value="">Select Type</option>
                                {registrationTypes.map((registrationType) => 
                                    <option key={registrationType} value={registrationType.toLowerCase()}>
                                        {registrationType}
                                    </option>
                                )}
                            </SelectInput>
                            
                            <div className="w-full md:flex md:justify-between">
                                <div className="w-full md:w-[50%] flex justify-between">
                                    <div className="w-[30%] md:w-[40%] mr-4">
                                        <SelectInput label="Prefix" required={true} value={form.registrationPrefix} onChange={(e) => searchCompanyInfo(e.target.value, "prefix")} validationName="registrationPrefix" checkFormValidation={checkFormValidation}>
                                            <option value="">Select</option>
                                            <option value="bn">BN</option>
                                            <option value="rc">RC</option>
                                        </SelectInput>
                                    </div>
                                    
                                    <div className="w-[70%] md:w-[60%] md:mr-4">
                                        <Input
                                            type="text"
                                            required={true}
                                            label="Registration Number"
                                            value={form.registrationNumber}
                                            onChange={(e) => searchCompanyInfo(e.target.value, "number")}
                                            validationName="registrationNumber"
                                            checkFormValidation={checkFormValidation}
                                            rightIconClicked={searchCompany}
                                            inputClass="!pr-24"
                                            // rightIconClass={companiesLoading ? "hover:bg-gray-200" : "hover:bg-ep-primary-200"}
                                            rightIcon={<div className="h-6 px-2 mr-2 flex items-center text-white text-xs font-semibold uppercase bg-ep-primary rounded-sm">
                                                Verify
                                                <FontAwesomeIcon icon="circle-notch" spin className={`ml-1 text-white ${!companiesLoading && "hidden"}`} />
                                            </div>}
                                        />
                                    </div>
                                </div>
                                    
                                <div className="w-[100%] md:w-[50%]">
                                    <Maybe condition={searchCompanyResponseType() === "multiple"}>
                                        <SelectInput label="Business Name" required={true} value={form.businessName} onChange={(e) => setForm({...form, businessName: e.target.value})} validationName="businessName" checkFormValidation={checkFormValidation}>
                                            <option value="">Select business</option>
                                            {Array.isArray(companies?.data) === true && companies?.data?.map((company, index) => (
                                                <option key={index} value={company?.approvedName}>{company?.approvedName}</option>
                                            ))}
                                        </SelectInput>
                                    </Maybe>
                                    
                                    <Maybe condition={searchCompanyResponseType() === "single"}>
                                        <Input type="text" label="Business Name" required={true} value={form.businessName} disabled />
                                    </Maybe>
                                    
                                    <Maybe condition={isCompanyNameSearched === true}>
                                    <div className="relative -top-4 text-xs">
                                        <Maybe condition={companiesLoading === true}>
                                            <div className="form-input-message !text-black">
                                                <FontAwesomeIcon icon="circle-notch" spin className="mr-1" />
                                                Searching business info...
                                            </div>
                                        </Maybe>
                                        <Maybe condition={form.fullRegistrationNumber !== "" && !companiesLoading}>
                                            <Maybe condition={form.businessName === "" && typeof companies?.data !== "string" && companies?.data?.length > 1}>
                                                <div className="form-input-message">
                                                    <FontAwesomeIcon icon="exclamation-circle" className="mr-1" />
                                                    Multiple results found. Please select your business name
                                                </div>
                                            </Maybe>
                                            <Maybe condition={form.businessName !== "" && typeof companies?.data === "string" && companies?.data !== ""}>
                                                <div className="form-input-message !text-green-500">
                                                    <FontAwesomeIcon icon="check-circle" className="mr-1" />
                                                    Business found
                                                </div>
                                            </Maybe>
                                            <Maybe condition={form.businessName === "" && companies?.status === "failed"}>
                                                <div className="form-input-message flex">
                                                    <FontAwesomeIcon icon="exclamation-circle" className="mt-1 mr-1" />
                                                    <div>
                                                        Business name not found.
                                                        <br />
                                                        Please recheck the business registration number.
                                                    </div>
                                                </div>
                                            </Maybe>
                                        </Maybe>
                                    </div>
                                    </Maybe>
                                </div>
                            </div>
                            
                            <Input type="text" label="Trading Name" required={true} value={form.tradingName} onChange={(e) => setForm({...form, tradingName: e.target.value})} validationName="tradingName" checkFormValidation={checkFormValidation} />
                            <div className="relative -top-4 text-xs text-fade">
                                A Trading name is your business alias
                            </div>
                            
                            <Input type="text" label="Business Address" required={true} value={form.businessAddress} onChange={(e) => setForm({...form, businessAddress: e.target.value})} validationName="businessAddress" checkFormValidation={checkFormValidation} />
                            
                            <div className="mt-6 mb-4">
                                <div className="font-bold">
                                    Business Document Verification
                                </div>
                                <div className="text-sm text-ep-gray-600">
                                    For verification, please upload a Certificate of Incorporation document for your business.
                                </div>
                            </div>
                            
                            <FileUploadForm
                                data={form.cacDocument}
                                onchange={(e) => setForm({...form, cacDocument: e})}
                                filetype={["jpg","jpeg","png","pdf"]}
                                filename="CAC verification document"
                                validationName="cacDocument"
                                checkFormValidation={checkFormValidation}
                            />
                             
                             <div className="mt-6 mb-4">
                                <div className="font-bold">
                                    Upload CAC Status Report, CAC 2 or CAC 7 document
                                </div>
                                <div className="text-sm text-ep-gray-600">
                                    Please upload your CAC Status Report or an equivalent detailing all the directors in your company
                                </div>
                            </div>
                            
                            <FileUploadForm
                                data={form.particularsOfDirectorsDocument}
                                onchange={(e) => setForm({...form, particularsOfDirectorsDocument: e})}
                                filetype={["jpg","jpeg","png","pdf"]}
                                filename="Particulars of directors document"
                                validationName="particularsOfDirectorsDocument"
                                checkFormValidation={checkFormValidation}
                            />
                                                    
                            <div className="mt-6 mb-4">
                                <div className="font-bold">
                                    Proof of Business Address
                                </div>
                                <div className="text-sm text-ep-gray-600">
                                    Please upload either of these documents such as Utility Bill, Lease of Tenancy Agreement, Reciept showing your Business Address, etc.
                                </div>
                            </div>
                            
                            <SelectInput label="Document Type" required={true} value={form.addressVerificationType} onChange={(e) => setForm({...form, addressVerificationType: e.target.value})} validationName="addressVerificationType" checkFormValidation={checkFormValidation}>
                                <option value="">Select Bill Type</option>
                                {Object.keys(addressDocumentLabel).map((doc) => 
                                    <option key={doc} value={doc.toLowerCase()}>
                                        {addressDocumentLabel[doc]}
                                    </option>
                                )}
                            </SelectInput>
                            
                            <Maybe condition={form.addressVerificationType !== ""}>
                                <FileUploadForm
                                    data={form.addressVerificationDocument}
                                    onchange={(e) => setForm({...form, addressVerificationDocument: e})}
                                    filetype={["jpg","jpeg","png","pdf"]}
                                    filename="Address verification document"
                                    validationName="addressVerificationDocument"
                                    checkFormValidation={checkFormValidation}
                                />
                            </Maybe>
                            
                            <div className="mt-8 flex justify-end space-x-4">
                                <ButtonLoader type="button" onClick={saveDraft} loading={getLoadingStatus() === true && exitAfterSave === false} className="btn btn-transparent-primary" disabled={!isFormValidated}>
                                    Save a Draft
                                </ButtonLoader>
                                <ButtonLoader type="submit" loading={getLoadingStatus() === true && exitAfterSave === true} className="btn btn-ep-primary" disabled={!isFormValidated}>
                                    Continue
                                </ButtonLoader>
                            </div>
                        </form>
                    </Maybe>
                    
                </div>
            </div>
            
        </div>
    )
}


export default VerifyBusiness;